import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useTranslation } from '../utils/i18n';
import { useCurrency } from '../utils/currency';
import { useTheme } from '../context/ThemeContext';
import {
  CogIcon,
  CurrencyDollarIcon,
  LanguageIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  BellIcon,
  BellSlashIcon,
  ClockIcon,
  SwatchIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

export default function GymOwnerSettingsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t, languageOptions, setLanguage } = useTranslation(user?.gym_id);
  const { formatCurrency, currencyOptions, setCurrencyCode } = useCurrency(user?.gym_id);
  const { accentColor, changeAccentColor, timezone, changeTimezone } = useTheme();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [gymData, setGymData] = useState(null);
  const [updating, setUpdating] = useState(false);

  const timezoneOptions = [
    { code: 'Asia/Kolkata', label: 'India (IST - UTC+05:30)', flag: '🇮🇳' },
    { code: 'America/New_York', label: 'United States (EST/EDT - UTC-05:00)', flag: '🇺🇸' },
    { code: 'America/Los_Angeles', label: 'United States West (PST/PDT - UTC-08:00)', flag: '🇺🇸' },
    { code: 'Europe/London', label: 'United Kingdom (GMT/BST - UTC+00:00)', flag: '🇬🇧' },
    { code: 'Asia/Dubai', label: 'United Arab Emirates (GST - UTC+04:00)', flag: '🇦🇪' },
    { code: 'Asia/Singapore', label: 'Singapore (SGT - UTC+08:00)', flag: '🇸🇬' },
    { code: 'Australia/Sydney', label: 'Australia (AEST/AEDT - UTC+10:00)', flag: '🇦🇺' },
    { code: 'Asia/Tokyo', label: 'Japan (JST - UTC+09:00)', flag: '🇯🇵' },
  ];

  const presetColors = [
    { name: 'Default Orange', hex: '#f97316' },
    { name: 'Red Vibrant', hex: '#ef4444' },
    { name: 'Crimson Red', hex: '#dc2626' },
    { name: 'Rose Pink', hex: '#f43f5e' },
    { name: 'Emerald Green', hex: '#10b981' },
    { name: 'Royal Blue', hex: '#3b82f6' },
    { name: 'Purple Neon', hex: '#a855f7' }
  ];

  useEffect(() => {
    fetchGymSettings();
  }, []);

  const fetchGymSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/gym/profile');
      setGymData(response.data.gym);
      if (response.data.gym?.accent_color) {
        changeAccentColor(response.data.gym.accent_color, user?.gym_id);
      }
    } catch (err) {
      console.error('Failed to fetch gym settings:', err);
      setError(err.response?.data?.error || t('messages.operationFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleCurrencyUpdate = async (newCurrency) => {
    if (updating) return;
    try {
      setUpdating(true);
      setError('');
      setSuccess('');

      const response = await api.put('/api/gym/profile', { currency: newCurrency });
      setCurrencyCode(newCurrency);
      setGymData(response.data.gym);
      setSuccess(t('messages.currencyUpdated', { currency: newCurrency }));
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || t('messages.operationFailed'));
    } finally {
      setUpdating(false);
    }
  };

  const handleLanguageUpdate = async (newLanguage) => {
    if (updating) return;
    try {
      setUpdating(true);
      setError('');
      setSuccess('');

      const response = await api.put('/api/gym/profile', { language: newLanguage });
      setLanguage(newLanguage);
      setGymData(response.data.gym);
      setSuccess(t('messages.languageUpdated', { language: languageOptions.find(l => l.code === newLanguage)?.name || newLanguage }));
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || t('messages.operationFailed'));
    } finally {
      setUpdating(false);
    }
  };

  const handleGymStatusToggle = async () => {
    if (updating) return;
    try {
      setUpdating(true);
      setError('');
      setSuccess('');

      const newValue = !gymData.show_gym_status;
      const response = await api.put('/api/gym/profile', { show_gym_status: newValue });
      setGymData(response.data.gym);
      setSuccess(`Gym Status ${newValue ? 'enabled' : 'disabled'}`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || t('messages.operationFailed'));
    } finally {
      setUpdating(false);
    }
  };

  const handleTimezoneChange = (newTz) => {
    changeTimezone(newTz);
    setSuccess(`Timezone set to ${newTz}`);
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleColorChange = async (newColor) => {
    changeAccentColor(newColor, user?.gym_id);
    if (user?.gym_id) {
      localStorage.setItem(`gym_accent_color_${user.gym_id}`, newColor);
    }
    try {
      await api.put('/api/gym/profile', { accent_color: newColor });
    } catch (err) {}
    setSuccess(`Gym Theme Accent Color updated to ${newColor}`);
    setTimeout(() => setSuccess(''), 3000);
  };

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-600">{t('common.loading')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm">
        <h1 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
          <CogIcon className="w-6 h-6 text-orange-600" /> {t('nav.settings', 'Settings')}
        </h1>
        <p className="text-gray-600">{t('settings.description', 'Configure your gym workspace settings, accent theme color, system preferences, and regional timezone.')}</p>
      </div>

      {/* Success/Error Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-4 rounded-xl flex items-center gap-2">
          <ExclamationCircleIcon className="w-5 h-5 text-red-500" />
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 text-sm p-4 rounded-xl flex items-center gap-2">
          <CheckCircleIcon className="w-5 h-5 text-green-500" />
          {success}
        </div>
      )}

      {/* Theme Accent Color Picker Section */}
      <div className="bg-white border border-gray-200 p-6 rounded-2xl shadow-sm space-y-4">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <SwatchIcon className="w-5 h-5 text-orange-600" /> System Accent Color Picker
        </h2>
        <p className="text-sm text-gray-600">
          Pick your preferred accent theme color for your gym workspace and members (default Orange <code className="font-mono text-xs bg-gray-100 px-1 py-0.5 rounded text-orange-600">#f97316</code> or choose a custom color).
        </p>

        <div className="flex flex-wrap items-center gap-3 pt-2">
          {presetColors.map((color) => (
            <button
              key={color.hex}
              onClick={() => handleColorChange(color.hex)}
              className={`px-3 py-2 rounded-xl text-xs font-bold text-white flex items-center gap-2 transition-all shadow-sm ${
                accentColor === color.hex ? 'ring-2 ring-offset-2 ring-gray-900 scale-105' : 'opacity-85 hover:opacity-100'
              }`}
              style={{ backgroundColor: color.hex }}
            >
              <span className="w-3 h-3 rounded-full bg-white/30"></span>
              {color.name}
            </button>
          ))}

          {/* Native HTML5 Color Picker */}
          <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 p-1.5 rounded-xl">
            <input
              type="color"
              value={accentColor}
              onChange={(e) => handleColorChange(e.target.value)}
              className="w-8 h-8 rounded-lg cursor-pointer border-0 bg-transparent"
              title="Click to pick custom color"
            />
            <span className="text-xs font-mono font-bold text-gray-800 pr-2">{accentColor}</span>
          </div>
        </div>
      </div>

      {/* Timezone Settings Section */}
      <div className="bg-white border border-gray-200 p-6 rounded-2xl shadow-sm space-y-4">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <ClockIcon className="w-5 h-5 text-red-600" /> Regional Timezone (IST Asia/Kolkata)
        </h2>
        <p className="text-sm text-gray-600">
          Configure system datetime calculations and attendance logs based on Indian Standard Time (IST) or international country timezones.
        </p>

        <select
          value={timezone}
          onChange={(e) => handleTimezoneChange(e.target.value)}
          className="w-full max-w-md bg-gray-50 border border-gray-200 focus:border-red-500 focus:ring-2 focus:ring-red-500/20 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none transition-all duration-200"
        >
          {timezoneOptions.map((tz) => (
            <option key={tz.code} value={tz.code}>
              {tz.flag} {tz.label}
            </option>
          ))}
        </select>

        <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-xs text-red-900 flex items-center gap-2">
          <SparklesIcon className="w-5 h-5 text-red-600 shrink-0" />
          <span>
            Current Selected Timezone: <strong className="font-mono">{timezone}</strong> • Server System Time synced with IST Asia/Kolkata.
          </span>
        </div>
      </div>

      {/* Currency Settings Section */}
      <div className="bg-white border border-gray-200 p-6 rounded-2xl shadow-sm">
        <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
          <CurrencyDollarIcon className="w-5 h-5 text-red-600" /> {t('settings.currencySettings')}
        </h2>
        
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('settings.selectCurrency')}
            </label>
            <p className="text-sm text-gray-600 mb-4">
              {t('settings.currencyDescription')}
            </p>
            
            <select
              value={gymData?.currency || 'INR'}
              onChange={(e) => handleCurrencyUpdate(e.target.value)}
              disabled={updating}
              className="w-full max-w-md bg-gray-50 border border-gray-200 focus:border-red-500 focus:ring-2 focus:ring-red-500/20 rounded-lg px-4 py-3 text-sm text-gray-900 focus:outline-none transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {currencyOptions.map((currency) => (
                <option key={currency.code} value={currency.code}>
                  {currency.code} - {currency.label} ({currency.symbol})
                </option>
              ))}
            </select>
            
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-900">
                <span className="font-medium">{t('settings.currentCurrency')}:</span> {gymData?.currency || 'INR'} ({currencyOptions.find(c => c.code === gymData?.currency)?.symbol || '₹'})
              </p>
              <p className="text-sm text-blue-700 mt-1">
                Example: {formatCurrency(1000)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Gym Status Settings Section */}
      <div className="bg-white border border-gray-200 p-6 rounded-2xl shadow-sm">
        <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
          {gymData?.show_gym_status ? (
            <BellIcon className="w-5 h-5 text-red-600" />
          ) : (
            <BellSlashIcon className="w-5 h-5 text-gray-400" />
          )}
          Gym Status Display
        </h2>
        
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Show Gym Status Badge
              </label>
              <p className="text-sm text-gray-600">
                When enabled, displays your gym's operational status (Open/Closed) on both Gym Owner and Member dashboards.
                The status automatically updates based on your login/logout activity.
              </p>
            </div>
            <button
              onClick={handleGymStatusToggle}
              disabled={updating}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${
                gymData?.show_gym_status ? 'bg-red-600' : 'bg-gray-300'
              } ${updating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
                  gymData?.show_gym_status ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Language Settings Section */}
      <div className="bg-white border border-gray-200 p-6 rounded-2xl shadow-sm">
        <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
          <LanguageIcon className="w-5 h-5 text-red-600" /> {t('settings.languageSettings')}
        </h2>
        
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('settings.selectLanguage')}
            </label>
            <p className="text-sm text-gray-600 mb-4">
              {t('settings.languageDescription')}
            </p>
            
            <select
              value={gymData?.language || 'en'}
              onChange={(e) => handleLanguageUpdate(e.target.value)}
              disabled={updating}
              className="w-full max-w-md bg-gray-50 border border-gray-200 focus:border-red-500 focus:ring-2 focus:ring-red-500/20 rounded-lg px-4 py-3 text-sm text-gray-900 focus:outline-none transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {languageOptions.map((language) => (
                <option key={language.code} value={language.code}>
                  {language.name} ({language.nativeName})
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}